// One feed of a record's «История» (R-U22): the journal's events, the saved revisions and the published versions by time, newest
// first. A revision is the state a save made, so it rides on the event of that save — the same command a moment apart — and gives
// that line «Сравнить» and «Вернуть»; a revision with no event in the feed (older than the journal's page) stands on its own.

export interface FeedEvent { kind: string; command: string; at: string }
export interface FeedSave { number: number; command: string | null; createdAt: string }
export interface FeedVersion { number: number; createdAt: string }
export interface FeedItem<E, S, V> { at: string; event?: E; save?: S; version?: V }

/** A save and its event are written in one transaction; the margin covers clocks of the journal and the revisions. */
const SAME_SAVE_MS = 2000;

export function mergeFeed<E extends FeedEvent, S extends FeedSave, V extends FeedVersion>(events: E[], saves: S[], versions: V[]): FeedItem<E, S, V>[] {
  const ms = (iso: string) => Date.parse(iso);
  const taken = new Set<E>();
  const items: FeedItem<E, S, V>[] = [];
  const byEvent = new Map<E, S>();
  for (const s of saves) {
    const e = s.command === null ? undefined : events
      .filter((x) => x.kind === 'record' && x.command === s.command && !taken.has(x) && Math.abs(ms(x.at) - ms(s.createdAt)) <= SAME_SAVE_MS)
      .sort((a, b) => Math.abs(ms(a.at) - ms(s.createdAt)) - Math.abs(ms(b.at) - ms(s.createdAt)))[0];
    if (e) { taken.add(e); byEvent.set(e, s); } else items.push({ at: s.createdAt, save: s });
  }
  for (const e of events) items.push({ at: e.at, event: e, save: byEvent.get(e) });
  for (const v of versions) items.push({ at: v.createdAt, version: v });
  // Newest first; at the same moment a version goes over the save that made it, a save with no event under its event.
  const rank = (x: FeedItem<E, S, V>) => (x.version ? 0 : x.event ? 1 : 2);
  return items.sort((a, b) => ms(b.at) - ms(a.at) || rank(a) - rank(b));
}
